import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ManualCheckInModal = ({ onClose, onSuccess }) => {
  const { user, currentTenant } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (searchQuery?.length >= 2) {
      searchMembers();
    } else {
      setMembers([]);
    }
  }, [searchQuery]);

  const searchMembers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('tenant_members')
        ?.select(`
          *,
          user_profiles!tenant_members_user_id_fkey(id, full_name, email, avatar_url)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true)
        ?.or(`user_profiles.full_name.ilike.%${searchQuery}%,user_profiles.email.ilike.%${searchQuery}%`)
        ?.limit(10);

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error searching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!selectedMember) return;

    setSubmitting(true);
    setError('');

    try {
      // Check if already checked in
      const { data: existingCheckIn } = await supabase
        ?.from('attendance_logs')
        ?.select('*')
        ?.eq('user_id', selectedMember?.user_profiles?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.is('check_out_time', null)
        ?.single();

      if (existingCheckIn) {
        setError('Member is already checked in');
        return;
      }

      // Create check-in record
      const { error: insertError } = await supabase
        ?.from('attendance_logs')
        ?.insert({
          tenant_id: currentTenant?.id,
          user_id: selectedMember?.user_profiles?.id,
          check_in_time: new Date()?.toISOString(),
          entry_type: 'manual',
          created_by: user?.id
        });

      if (insertError) throw insertError;

      onSuccess();
    } catch (error) {
      console.error('Error checking in member:', error);
      setError('Failed to check in member. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-bold">Manual Check-in</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Search Member
            </label>
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Enter name or email"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Type at least 2 characters to search
            </p>
          </div>

          {/* Search Results */}
          {loading && (
            <div className="text-center py-4">
              <Icon name="Loader" size={24} className="animate-spin mx-auto text-muted-foreground" />
            </div>
          )}

          {!loading && members?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Select Member:</p>
              {members?.map((member) => (
                <button
                  key={member?.id}
                  onClick={() => setSelectedMember(member)}
                  className={`w-full p-3 rounded-lg border-2 transition-colors text-left ${
                    selectedMember?.id === member?.id
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  }`}
                  disabled={submitting}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                      {member?.user_profiles?.avatar_url ? (
                        <img
                          src={member?.user_profiles?.avatar_url}
                          alt={member?.user_profiles?.full_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Icon name="User" size={20} className="text-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">
                        {member?.user_profiles?.full_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {member?.user_profiles?.email}
                      </p>
                    </div>
                    {selectedMember?.id === member?.id && (
                      <Icon name="CheckCircle" size={20} className="text-primary" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && searchQuery?.length >= 2 && members?.length === 0 && (
            <div className="text-center py-4">
              <Icon name="Search" size={32} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No members found</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-sm text-destructive flex items-center gap-2">
                <Icon name="AlertCircle" size={16} />
                {error}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCheckIn}
            className="flex-1"
            disabled={!selectedMember || submitting}
            loading={submitting}
            iconName="Check"
          >
            Check In
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManualCheckInModal;